/** Headline cards (A7Y-4883): corpus total, what the public endpoint shows, recent and 7-day arrivals, data freshness. */
import type { KeywordDatabaseBlockMeta, KeywordDatabaseHeadline } from '@a7y/types';
import { formatInt, formatUtcInstant } from '../format';

function Card({
  title,
  testId,
  value,
  children,
}: {
  title: string;
  testId: string;
  value: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700"
    >
      <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{title}</p>
      <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums text-zinc-950 dark:text-white">
        {value}
      </p>
      {children && <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{children}</p>}
    </div>
  );
}

export function HeadlineStrip({
  headline,
  meta,
}: {
  headline: KeywordDatabaseHeadline;
  meta: KeywordDatabaseBlockMeta;
}) {
  const pub = headline.publicDisplay;
  return (
    <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
      <Card
        title="Keywords in the database"
        testId="headline-total"
        value={formatInt(headline.total)}
      >
        counted through {headline.asOfDate}
      </Card>
      {headline.publicDisplayUnreadable ? (
        <Card title="Public endpoint currently displays" testId="headline-public" value="—">
          public figure unreadable (KV read failed)
        </Card>
      ) : pub ? (
        <Card
          title="Public endpoint currently displays"
          testId="headline-public"
          value={formatInt(pub.displayCount)}
        >
          as of {pub.asOf}; cached {formatUtcInstant(pub.generatedAt)}; can lag up to 24 h
        </Card>
      ) : (
        <Card title="Public endpoint currently displays" testId="headline-public" value="—">
          no cached public figure
        </Card>
      )}
      <Card
        title="New in the last 24 h"
        testId="headline-new24h"
        value={formatInt(headline.new24h)}
      >
        {headline.new24hListWeekDated > 0
          ? `incl. ${formatInt(headline.new24hListWeekDated)} list-week-dated`
          : 'first seen in the trailing 24 h'}
      </Card>
      <Card
        title="7-day average per day"
        testId="headline-avg7d"
        value={formatInt(headline.avgDaily7d)}
      >
        trailing 168 h of first sightings / 7
      </Card>
      <Card title="Data as of" testId="headline-as-of" value={formatUtcInstant(meta.generatedAt)}>
        source: {meta.source}; cached up to {Math.round(meta.cacheTtlSec / 60)} min
      </Card>
    </div>
  );
}
